import React, { useState } from 'react';
import { Button, Textarea } from '@nextui-org/react';
import { Send, X } from 'lucide-react';
import commentService from '../../services/commentService';
import { t } from '../../i18n';
import { appAlert } from '../alerts/appAlert';

const MAX_LENGTH = 500;

/**
 * Wiederverwendbarer Kommentar-Composer:
 * Textarea mit 500-Zeichen-Counter, Enter = senden, Shift+Enter = Umbruch.
 * Fehlerbehandlung inklusive (Backend kann Plaintext-Fehler liefern).
 */
const CommentComposer: React.FC<{
  onSubmit: (content: string) => Promise<void>;
  placeholder?: string;
  submitLabel?: string;
  autoFocus?: boolean;
  compact?: boolean;
  onCancel?: () => void;
}> = ({
  onSubmit,
  placeholder = t('web.page.setup-detail-page.schreibe-einen-kommentar'),
  submitLabel = t('web.page.setup-detail-page.kommentieren'),
  autoFocus = false,
  compact = false,
  onCancel,
}) => {
  const [value, setValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = async () => {
    const content = value.trim();
    if (!content || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSubmit(content.slice(0, MAX_LENGTH));
      setValue('');
    } catch (err: any) {
      appAlert.error(
        commentService.extractErrorMessage(
          err,
          t('web.component.comment-composer.kommentar-konnte-nicht-gespeichert-werde')
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div
      className={`rounded-2xl border border-zinc-800 bg-zinc-900/70 ${
        compact ? 'p-3' : 'p-4'
      }`}
    >
      <Textarea
        placeholder={placeholder}
        value={value}
        onValueChange={setValue}
        onKeyDown={handleKeyDown}
        maxLength={MAX_LENGTH}
        minRows={compact ? 1 : 2}
        autoFocus={autoFocus}
        variant="bordered"
        classNames={{
          inputWrapper:
            'bg-zinc-950 border-zinc-700 data-[hover=true]:border-zinc-600 group-data-[focus=true]:border-[#3fced1]',
          input: 'text-white placeholder:text-gray-500',
        }}
      />
      <div className="mt-3 flex items-center justify-between gap-2">
        <span className="text-xs text-zinc-500 tabular-nums">
          {value.length} / {MAX_LENGTH}
        </span>
        <div className="flex items-center gap-2">
          {onCancel && (
            <Button
              size="sm"
              variant="light"
              className="text-zinc-400"
              startContent={<X size={14} />}
              onPress={onCancel}
            >
              {t('web.component.registrations-manager.abbrechen')}
            </Button>
          )}
          <Button
            size="sm"
            className="bg-[#3fced1] hover:bg-[#35b5b8] text-black font-semibold"
            startContent={<Send size={14} />}
            isLoading={isSubmitting}
            isDisabled={!value.trim()}
            onPress={submit}
          >
            {submitLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CommentComposer;
